import React, { useCallback, useEffect, useRef, useState } from 'react';
import { initiatePayment } from '../../razorpayService';
import {
  createUpiQr,
  fetchBankDetails,
  fetchBankTransferRequests,
  fetchQrStatus,
  submitBankTransferRequest,
} from '../../services/walletApi';
import {
  MIN_BANK_TRANSFER_INR,
  MIN_WALLET_RECHARGE_INR,
  QUICK_WALLET_RECHARGE_AMOUNTS,
} from '../../utils/bookingPayment';

const QUICK_AMOUNTS = QUICK_WALLET_RECHARGE_AMOUNTS;

function formatQuickAmountLabel(amt) {
  if (amt < 1000) return `₹${amt}`;
  return `₹${(amt / 1000).toFixed(amt % 1000 === 0 ? 0 : 1)}K`;
}

const PAYMENT_METHODS = [
  { id: 'upi', label: 'UPI', kind: 'upi_qr' },
  { id: 'neft', label: 'NEFT', kind: 'bank', bankMethod: 'NEFT' },
  { id: 'imps', label: 'IMPS', kind: 'bank', bankMethod: 'IMPS' },
  { id: 'card', label: 'Card', kind: 'razorpay', checkoutMethod: 'card' },
  { id: 'netbanking', label: 'Net Banking', kind: 'razorpay', checkoutMethod: 'netbanking' },
];

const methodCardStyle = (active) => ({
  padding: '14px 16px',
  borderRadius: 12,
  cursor: 'pointer',
  border: active
    ? '1px solid rgba(201,168,76,0.5)'
    : '1px solid rgba(255,255,255,0.07)',
  background: active ? 'rgba(201,168,76,0.06)' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 8,
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
});

const methodIconStyle = (active) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  background: active ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.04)',
});

const methodCheckStyle = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: '#C9A84C',
  color: '#0a1628',
  fontSize: 10,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  flexShrink: 0,
};

const pillStyle = (active) => ({
  padding: '6px 14px',
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  minHeight: 32,
  color: active ? '#0a1628' : '#C9A84C',
  background: active ? 'linear-gradient(135deg, #C9A84C, #a8893e)' : 'transparent',
  border: active ? 'none' : '1px solid rgba(201,168,76,0.35)',
});

export default function WalletRechargePanel({
  t,
  userId,
  userName,
  userEmail,
  userPhone,
  rechargeAmt,
  setRechargeAmt,
  onBalanceUpdated,
  isMobile,
}) {
  const [selectedMethod, setSelectedMethod] = useState('upi');
  const [upiQr, setUpiQr] = useState(null);
  const [upiLoading, setUpiLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [bankMethod, setBankMethod] = useState('NEFT');
  const [bankUtr, setBankUtr] = useState('');
  const [bankSubmitting, setBankSubmitting] = useState(false);
  const [bankRequests, setBankRequests] = useState([]);
  const pollRef = useRef(null);

  const parsedAmt = parseInt(rechargeAmt, 10);
  const activeDef = PAYMENT_METHODS.find((m) => m.id === selectedMethod) || PAYMENT_METHODS[0];
  const isUpi = selectedMethod === 'upi';
  const isRazorpay = selectedMethod === 'card' || selectedMethod === 'netbanking';
  const isBank = selectedMethod === 'neft' || selectedMethod === 'imps';
  const displayAmounts = isBank
    ? QUICK_AMOUNTS.filter((a) => a >= 5000)
    : QUICK_AMOUNTS.filter((a) => a >= 500);

  const loadBankMeta = useCallback(async () => {
    try {
      const details = await fetchBankDetails();
      setBankDetails(details);
    } catch {
      setBankDetails(null);
    }
    if (userId) {
      try {
        const { items } = await fetchBankTransferRequests(userId);
        setBankRequests(items || []);
      } catch {
        setBankRequests([]);
      }
    }
  }, [userId]);

  useEffect(() => {
    if (activeDef.kind === 'bank') loadBankMeta();
  }, [activeDef.kind, loadBankMeta]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const ensureSession = () => {
    if (!userId) {
      alert(t('walletLoginRequired'));
      return false;
    }
    return true;
  };

  const validateAmount = () => {
    const minInr = activeDef.kind === 'bank' ? MIN_BANK_TRANSFER_INR : MIN_WALLET_RECHARGE_INR;
    if (!parsedAmt || parsedAmt < minInr) {
      alert(activeDef.kind === 'bank' ? t('minimumBankRecharge') : t('minimumRecharge'));
      return false;
    }
    return true;
  };

  const selectMethod = (def) => {
    setSelectedMethod(def.id);
    if (def.bankMethod) setBankMethod(def.bankMethod);
    if (def.kind !== 'upi_qr') {
      stopPolling();
      setUpiQr(null);
    }
  };

  const mapUpiQrError = (message) => {
    const text = (message || '').toString();
    if (/requested url was not found/i.test(text)) return t('upiQrNotEnabledOnMerchant');
    if (/rs\.1.?10 test|card tab/i.test(text)) return t('upiQrUseCardForSmallAmounts');
    return text || t('upiQrFailed');
  };

  const startQrPolling = (qrCodeId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchQrStatus(qrCodeId);
        if (status.paid) {
          stopPolling();
          onBalanceUpdated(status.newBalance ?? null, parsedAmt, status.paymentId);
          setUpiQr(null);
        } else if (status.qrStatus === 'closed') {
          stopPolling();
        }
      } catch {
        /* retry */
      }
    }, 5000);
  };

  const handleRazorpayPay = async (checkoutMethod) => {
    if (!ensureSession() || !validateAmount()) return;
    setPayLoading(true);
    try {
      await initiatePayment({
        amount: parsedAmt,
        userId,
        name: userName,
        email: userEmail,
        phone: userPhone,
        checkoutMethod,
        description: 'Development Express Wallet Recharge',
        onSuccess: async (response) => {
          const newBalance = response?.verification?.newBalance;
          onBalanceUpdated(newBalance, parsedAmt, response?.razorpay_payment_id);
        },
        onFailure: () => alert(`${String.fromCodePoint(0x274C)} ${t('paymentFailed')}`),
      });
    } finally {
      setPayLoading(false);
    }
  };

  const handleGenerateUpiQr = async () => {
    if (!ensureSession() || !validateAmount()) return;
    if (parsedAmt <= 10) {
      alert(t('upiQrUseCardForSmallAmounts'));
      return;
    }
    setUpiLoading(true);
    stopPolling();
    setUpiQr(null);
    try {
      const qr = await createUpiQr(userId, parsedAmt);
      setUpiQr(qr);
      startQrPolling(qr.qrCodeId);
    } catch (err) {
      alert(mapUpiQrError(err.message));
    } finally {
      setUpiLoading(false);
    }
  };

  const handleBankSubmit = async () => {
    if (!ensureSession() || !validateAmount()) return;
    setBankSubmitting(true);
    try {
      await submitBankTransferRequest({
        userId,
        amount: parsedAmt,
        method: bankMethod,
        utr: bankUtr,
      });
      alert(t('bankTransferSubmitted'));
      setBankUtr('');
      await loadBankMeta();
    } catch (err) {
      alert(err.message || t('bankTransferFailed'));
    } finally {
      setBankSubmitting(false);
    }
  };

  const primaryBtnStyle = {
    width: '100%',
    padding: 14,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #C9A84C, #a8893e)',
    border: 'none',
    color: '#0a1628',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 14,
  };

  const amountPlaceholder = t('rechargeCustomPlaceholder');
  const selectedAmt = rechargeAmt === '' ? null : rechargeAmt;

  return (
    <div className="relative z-[2]" style={{ pointerEvents: 'auto' }}>
      <div style={{
        fontSize: 12,
        color: '#8896a8',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
      >
        Add Money
      </div>

      <div style={{
        background: 'rgba(201,168,76,0.05)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: 12,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
      }}
      >
        <span style={{ fontSize: 20, color: '#C9A84C', fontWeight: 700 }}>₹</span>
        <input
          type="number"
          inputMode="numeric"
          placeholder={amountPlaceholder === 'rechargeCustomPlaceholder' ? 'Enter amount' : amountPlaceholder}
          value={rechargeAmt}
          onChange={(e) => setRechargeAmt(e.target.value)}
          min={isBank ? MIN_BANK_TRANSFER_INR : MIN_WALLET_RECHARGE_INR}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: 22,
            fontWeight: 700,
            color: '#e8e0d0',
            flex: 1,
            minWidth: 0,
            fontFamily: 'inherit',
            padding: 0,
          }}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 6 : 8, marginBottom: 16 }}>
        {displayAmounts.map((amt) => {
          const selected = selectedAmt === amt.toString();
          return (
            <button
              key={amt}
              type="button"
              style={{
                padding: '6px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                color: selected ? '#C9A84C' : '#8896a8',
                background: selected ? 'rgba(201,168,76,0.08)' : 'transparent',
                border: selected
                  ? '1px solid rgba(201,168,76,0.3)'
                  : '1px solid rgba(255,255,255,0.08)',
              }}
              onClick={() => setRechargeAmt(amt.toString())}
            >
              {formatQuickAmountLabel(amt)}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => { if (ensureSession()) selectMethod(PAYMENT_METHODS[0]); }}
        style={methodCardStyle(isUpi)}
        aria-pressed={isUpi}
      >
        <div style={methodIconStyle(isUpi)}>📱</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>UPI QR</div>
          <div style={{ fontSize: 11, color: '#8896a8' }}>Scan & pay instantly · from ₹1</div>
        </div>
        {isUpi && <div style={methodCheckStyle}>✓</div>}
      </button>

      <button
        type="button"
        onClick={() => { if (ensureSession()) selectMethod(PAYMENT_METHODS[3]); }}
        style={methodCardStyle(isRazorpay)}
        aria-pressed={isRazorpay}
      >
        <div style={methodIconStyle(isRazorpay)}>💳</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>Card / Net Banking</div>
          <div style={{ fontSize: 11, color: '#8896a8' }}>Credit, Debit, UPI apps · instant</div>
        </div>
        {isRazorpay && <div style={methodCheckStyle}>✓</div>}
      </button>

      <button
        type="button"
        onClick={() => { if (ensureSession()) selectMethod(PAYMENT_METHODS[1]); }}
        style={methodCardStyle(isBank)}
        aria-pressed={isBank}
      >
        <div style={methodIconStyle(isBank)}>🏦</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e8e0d0' }}>Bank Transfer</div>
          <div style={{ fontSize: 11, color: '#8896a8' }}>NEFT / IMPS · min ₹5,000 · 1-4 hrs</div>
        </div>
        <span style={{
          fontSize: 10,
          padding: '3px 8px',
          borderRadius: 20,
          background: 'rgba(255,152,0,0.1)',
          color: '#FF9800',
          border: '1px solid rgba(255,152,0,0.2)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
        >
          Large amounts
        </span>
        {isBank && <div style={{ ...methodCheckStyle, marginLeft: 8 }}>✓</div>}
      </button>

      {isRazorpay && (
        <div style={{ display: 'flex', gap: 8, margin: '4px 0 8px' }}>
          <button type="button" style={pillStyle(selectedMethod === 'card')} onClick={() => selectMethod(PAYMENT_METHODS[3])}>
            Card
          </button>
          <button type="button" style={pillStyle(selectedMethod === 'netbanking')} onClick={() => selectMethod(PAYMENT_METHODS[4])}>
            Net Banking
          </button>
        </div>
      )}

      {isBank && (
        <div style={{ display: 'flex', gap: 8, margin: '4px 0 8px' }}>
          <button
            type="button"
            style={pillStyle(bankMethod === 'NEFT')}
            onClick={() => selectMethod(PAYMENT_METHODS[1])}
          >
            NEFT
          </button>
          <button
            type="button"
            style={pillStyle(bankMethod === 'IMPS')}
            onClick={() => selectMethod(PAYMENT_METHODS[2])}
          >
            IMPS
          </button>
        </div>
      )}

      {isUpi && parsedAmt > 0 && parsedAmt <= 10 && (
        <p style={{ color: '#C9A84C', fontSize: 11, margin: '8px 0 0', lineHeight: 1.35 }}>{t('upiQrSmallAmountHint')}</p>
      )}
      {isUpi && upiQr?.imageUrl && (
        <div className="rounded-xl bg-white p-4 text-center" style={{ marginTop: 12 }}>
          <img
            src={upiQr.imageUrl}
            alt="UPI QR"
            className="mx-auto block h-auto w-full max-w-[180px] object-contain"
          />
        </div>
      )}

      {isBank && (
        <div style={{ fontSize: 12, color: '#c9a84c', marginTop: 8 }}>
          {!bankDetails?.configured ? (
            <p style={{ color: '#e94560', margin: '0 0 8px' }}>{t('bankTransferUnavailable')}</p>
          ) : (
            <div style={{ color: '#8896a8', lineHeight: 1.6, marginBottom: 12, fontSize: 12 }}>
              <div style={{ color: '#e8e0d0', fontWeight: 600, marginBottom: 4 }}>{t('bankTransferTitle')} ({bankMethod})</div>
              <div>{bankDetails.accountName}</div>
              <div>{bankDetails.bankName}</div>
              <div>A/C: {bankDetails.accountNumber}</div>
              <div>IFSC: {bankDetails.ifsc}</div>
            </div>
          )}
          <input
            className="box-border min-h-[44px] w-full rounded-lg border border-de-gold/30 bg-black/40 px-3 py-2 text-sm text-white"
            placeholder={t('bankUtrPlaceholder')}
            value={bankUtr}
            onChange={(e) => setBankUtr(e.target.value)}
          />
          <p style={{ color: '#8896a8', fontSize: 10, margin: '8px 0 0' }}>{t('bankTransferPendingHint')}</p>
          {bankRequests.length > 0 && (
            <ul style={{ marginTop: 12, padding: 0, listStyle: 'none', color: '#8896a8', maxHeight: 140, overflowY: 'auto' }}>
              {bankRequests.map((r) => (
                <li key={r.id} style={{ marginBottom: 6 }}>
                  ₹{Number(r.amount).toLocaleString('en-IN')} · {r.method} · {r.utr} · <strong>{r.status}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isUpi && (
        <button
          type="button"
          style={{ ...primaryBtnStyle, opacity: upiLoading ? 0.7 : 1 }}
          disabled={upiLoading}
          onClick={handleGenerateUpiQr}
        >
          {upiLoading ? t('pleaseWait') : t('generateUpiQr')}
        </button>
      )}
      {isRazorpay && (
        <button
          type="button"
          style={{ ...primaryBtnStyle, opacity: payLoading ? 0.7 : 1 }}
          disabled={payLoading}
          onClick={() => handleRazorpayPay(activeDef.checkoutMethod)}
        >
          {payLoading
            ? t('pleaseWait')
            : `${t('payNowText')} — ${activeDef.label}${parsedAmt >= MIN_WALLET_RECHARGE_INR ? ` (₹${parsedAmt.toLocaleString('en-IN')})` : ''}`}
        </button>
      )}
      {isBank && (
        <button
          type="button"
          style={{ ...primaryBtnStyle, opacity: (bankSubmitting || !bankDetails?.configured) ? 0.5 : 1 }}
          disabled={bankSubmitting || !bankDetails?.configured}
          onClick={handleBankSubmit}
        >
          {bankSubmitting ? t('pleaseWait') : t('submitBankTransfer')}
        </button>
      )}
    </div>
  );
}
